/**
 * ForceDirectedGraph.tsx - Force-directed graph layout using D3
 * 
 * CHANGE HISTORY:
 * - 251101_0846 [pending] danielbeer: Complete refactor with Tooltip, ContextMenu, proper zoom/pan
 * - 251031_2050 [pending] danielbeer: Created from Modulr's DependencyGraph with generic types
 * 
 * Physics-based layout where nodes repel each other and edges act as springs.
 * Best for: Exploring relationships, finding clusters, general network visualization
 * 
 * NEW FEATURES:
 * - Tooltip on hover with node details
 * - Context menu on right-click
 * - Proper zoom/pan (scroll=pan, controlled zoom via props)
 * - Node dragging
 * - Connected node highlighting
 * - Export support
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as d3 from 'd3';
import { Tooltip } from '../components/Tooltip';
import { useTooltip } from '../components/Tooltip/useTooltip';
import { ContextMenu } from '../components/ContextMenu';
import { useContextMenu } from '../components/ContextMenu/useContextMenu';
import type { GraphNode, GraphEdge, GraphStyle, ForceLayoutOptions } from './types';

// Context menu data types
interface NodeContextData<T = any> {
  type: 'node';
  node: GraphNode<T>;
}

interface EdgeContextData {
  type: 'edge';
  edge: GraphEdge;
}

type GraphContextData<T = any> = NodeContextData<T> | EdgeContextData;

export interface ForceDirectedGraphProps<T = any> {
  // Data
  data: {
    nodes: GraphNode<T>[];
    edges: GraphEdge[];
  };
  
  // Dimensions
  width?: number;
  height?: number;
  
  // Zoom/Pan (controlled externally by GraphViewer, optional with defaults)
  zoomLevel?: number;
  panOffset?: { x: number; y: number };
  panMode?: 'scroll' | 'drag';
  panSensitivity?: number;
  nodeSpacing?: number;
  enableZoom?: boolean;  // NEW: Enable/disable zoom behavior (default: false)
  
  // Display
  showLabels?: boolean;
  highlightConnected?: boolean;
  highlightNode?: string;
  
  // Styling
  style?: GraphStyle;
  forceOptions?: ForceLayoutOptions;
  
  // Callbacks
  onNodeClick?: (node: GraphNode<T>, event: React.MouseEvent) => void;
  onNodeHover?: (node: GraphNode<T> | null, event: React.MouseEvent) => void;
  onEdgeClick?: (edge: GraphEdge, event: React.MouseEvent) => void;
  onBackgroundClick?: (event: React.MouseEvent) => void;
  onZoomChange?: (zoomLevel: number, panOffset: { x: number; y: number }) => void;
  
  // Export
  onRequestSVG?: () => SVGSVGElement | null;
  
  className?: string;
}

interface D3Node extends d3.SimulationNodeDatum, GraphNode {
  x?: number;
  y?: number;
  fx?: number | null;
  fy?: number | null;
}

interface D3Link extends d3.SimulationLinkDatum<D3Node> {
  id?: string;
  source: string | D3Node;
  target: string | D3Node;
  label?: string;
  type?: string;
  weight?: number;
  color?: string;
  metadata?: Record<string, any>;
}

/**
 * ForceDirectedGraph Component
 * 
 * Fully integrated with Viewr's Tooltip and ContextMenu systems
 */
export const ForceDirectedGraph = <T = any,>({
  data,
  width = 900,
  height = 600,
  zoomLevel = 1,
  panOffset = { x: 0, y: 0 },
  panMode = 'drag',
  panSensitivity = 1.5,
  nodeSpacing = 100,
  enableZoom = false,  // DISABLED BY DEFAULT - prevents interference with nodes
  showLabels = true,
  highlightConnected = true,
  highlightNode,
  style = {},
  forceOptions = {},
  onNodeClick,
  onNodeHover,
  onEdgeClick,
  onBackgroundClick,
  onZoomChange,
  onRequestSVG,
  className = '',
}: ForceDirectedGraphProps<T>) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<SVGGElement | null>(null);
  const simulationRef = useRef<d3.Simulation<D3Node, D3Link> | null>(null);
  
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  
  // Tooltip integration with hover support
  const { tooltip, showTooltip, hideTooltip, cancelHide, updatePosition } = useTooltip();
  
  // Context menu integration with proper typing
  const { contextMenu, openContextMenu, closeContextMenu } = useContextMenu<GraphContextData<T>>();

  // Expose SVG element for export
  useEffect(() => {
    if (onRequestSVG) {
      // This would need to be called from parent
    }
  }, [onRequestSVG]);

  // Render tooltip content for a node
  const renderNodeTooltip = useCallback((node: GraphNode<T>) => {
    const entries = Object.entries(node.data || {}).filter(([key]) => 
      !['x', 'y', 'fx', 'fy', 'index', 'vx', 'vy'].includes(key)
    );
    
    return (
      <table>
        <tbody>
          <tr><td><strong>ID:</strong></td><td>{node.id}</td></tr>
          <tr><td><strong>Label:</strong></td><td>{node.label}</td></tr>
          {node.group && <tr><td><strong>Group:</strong></td><td>{node.group}</td></tr>}
          {entries.length > 0 && (
            <>
              <tr><td colSpan={2}><hr style={{ margin: '8px 0', border: 'none', borderTop: '1px solid #e5e7eb' }} /></td></tr>
              {entries.slice(0, 5).map(([key, value]) => (
                <tr key={key}>
                  <td><strong>{key}:</strong></td>
                  <td>{String(value)}</td>
                </tr>
              ))}
              {entries.length > 5 && (
                <tr><td colSpan={2}><em>...and {entries.length - 5} more</em></td></tr>
              )}
            </>
          )}
        </tbody>
      </table>
    );
  }, []);

  // Context menu items for nodes
  const getNodeContextMenuItems = useCallback((node: GraphNode<T>) => [
    {
      id: 'details',
      label: 'View Details',
      icon: 'ℹ️',
      action: () => {
        if (onNodeClick) {
          onNodeClick(node, {} as React.MouseEvent);
        }
        closeContextMenu();
      },
    },
    {
      id: 'copy-id',
      label: 'Copy Node ID',
      icon: '📋',
      shortcut: 'Ctrl+C',
      action: () => {
        navigator.clipboard.writeText(node.id);
        closeContextMenu();
      },
    },
    {
      id: 'copy-label',
      label: 'Copy Label',
      icon: '📝',
      action: () => {
        navigator.clipboard.writeText(node.label);
        closeContextMenu();
      },
    },
    { separator: true },
    {
      id: 'highlight',
      label: 'Highlight Connected',
      icon: '💡',
      action: () => {
        setHoveredNode(node.id);
        closeContextMenu();
      },
    },
    { separator: true },
    {
      id: 'export-node',
      label: 'Export Node Data',
      icon: '💾',
      action: () => {
        const jsonData = JSON.stringify(node, null, 2);
        const blob = new Blob([jsonData], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `node-${node.id}.json`;
        a.click();
        URL.revokeObjectURL(url);
        closeContextMenu();
      },
    },
  ], [onNodeClick, closeContextMenu]);

  // Context menu items for edges
  const getEdgeContextMenuItems = useCallback((edge: GraphEdge) => [
    {
      id: 'copy-edge',
      label: 'Copy Edge Info',
      icon: '📋',
      action: () => {
        const info = `${edge.source} → ${edge.target}${edge.label ? ` (${edge.label})` : ''}`;
        navigator.clipboard.writeText(info);
        closeContextMenu();
      },
    },
    {
      id: 'export-edge',
      label: 'Export Edge Data',
      icon: '💾',
      action: () => {
        const jsonData = JSON.stringify(edge, null, 2);
        const blob = new Blob([jsonData], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `edge-${edge.source}-${edge.target}.json`;
        a.click();
        URL.revokeObjectURL(url);
        closeContextMenu();
      },
    },
  ], [closeContextMenu]);

  useEffect(() => {
    if (!svgRef.current || data.nodes.length === 0) return;

    // Clear previous content
    d3.select(svgRef.current).selectAll('*').remove();

    const svg = d3.select(svgRef.current);

    // Create container for graph (will be transformed for zoom/pan)
    const container = svg.append('g');
    containerRef.current = container.node();

    // Apply zoom/pan transform
    container.attr('transform', `translate(${panOffset.x}, ${panOffset.y}) scale(${zoomLevel})`);

    // Background click handler
    svg.on('click', (event) => {
      if (event.target === svgRef.current && onBackgroundClick) {
        onBackgroundClick(event);
      }
      closeContextMenu();
    });

    // Prepare nodes
    const nodes: D3Node[] = data.nodes.map((node) => ({
      ...node,
      x: width / 2 + (Math.random() - 0.5) * 100,
      y: height / 2 + (Math.random() - 0.5) * 100,
    }));

    // Create node ID set for validation
    const nodeIds = new Set(nodes.map(n => n.id));

    // Prepare links - only include valid connections
    const links: D3Link[] = data.edges
      .filter((edge) => nodeIds.has(edge.source as string) && nodeIds.has(edge.target as string))
      .map((edge) => ({ ...edge }));

    // Force simulation options
    // Use nodeSpacing prop to control link distance and charge strength
    const {
      linkDistance = nodeSpacing,
      chargeStrength = -nodeSpacing,  // Direct scaling: larger spacing = stronger repulsion
      centerStrength = 0.1,
      collisionRadius = 30,
    } = forceOptions;

    // Create force simulation with velocity decay for smoother animations
    const simulation = d3.forceSimulation<D3Node>(nodes)
      .force('link', d3.forceLink<D3Node, D3Link>(links)
        .id((d) => d.id)
        .distance(linkDistance))
      .force('charge', d3.forceManyBody().strength(chargeStrength))
      .force('center', d3.forceCenter(width / 2, height / 2).strength(centerStrength))
      .force('collision', d3.forceCollide<D3Node>().radius(collisionRadius))
      .velocityDecay(0.4)  // Smoother deceleration (default: 0.4, lower = smoother)
      .alphaDecay(0.02);   // Slower cooldown for smoother transitions (default: 0.0228)

    simulationRef.current = simulation;

    // Create arrow markers
    const defs = container.append('defs');
    defs.append('marker')
      .attr('id', 'arrowhead')
      .attr('viewBox', '0 -5 10 10')
      .attr('refX', 20)
      .attr('refY', 0)
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .attr('orient', 'auto')
      .append('path')
      .attr('d', 'M0,-5L10,0L0,5')
      .attr('fill', typeof style.edgeColor === 'string' ? style.edgeColor : '#999');

    // Draw links
    const link = container.append('g')
      .attr('class', 'links')
      .selectAll('line')
      .data(links)
      .join('line')
      .attr('stroke', (d) => 
        typeof style.edgeColor === 'function' ? style.edgeColor(d as any) :
        d.color || style.edgeColor || '#999'
      )
      .attr('stroke-opacity', 0.6)
      .attr('stroke-width', (d) =>
        typeof style.edgeWidth === 'function' ? style.edgeWidth(d as any) :
        style.edgeWidth || 2
      )
      .attr('marker-end', 'url(#arrowhead)')
      .style('cursor', onEdgeClick ? 'pointer' : 'default')
      .on('click', (event, d) => {
        if (onEdgeClick) {
          event.stopPropagation();
          // Convert D3Link back to GraphEdge for callback
          const edge: GraphEdge = {
            source: typeof d.source === 'object' ? d.source.id : d.source,
            target: typeof d.target === 'object' ? d.target.id : d.target,
            label: d.label,
            type: d.type,
            weight: d.weight,
            color: d.color,
            metadata: d.metadata,
          };
          onEdgeClick(edge, event);
        }
      })
      .on('contextmenu', (event, d) => {
        event.preventDefault();
        event.stopPropagation();
        const edge: GraphEdge = {
          source: typeof d.source === 'object' ? d.source.id : d.source,
          target: typeof d.target === 'object' ? d.target.id : d.target,
          label: d.label,
          type: d.type,
          weight: d.weight,
          color: d.color,
          metadata: d.metadata,
        };
        openContextMenu(event, { type: 'edge', edge });
      });

    // Draw nodes
    const node = container.append('g')
      .attr('class', 'nodes')
      .selectAll('g')
      .data(nodes)
      .join('g')
      .attr('class', 'node')  // CRITICAL: Add 'node' class so zoom filter can identify it
      .style('cursor', 'pointer')
      .style('pointer-events', 'all');  // Ensure nodes capture their own events

    // Enable node dragging - with filter to prevent zoom interference
    const drag = d3.drag<any, D3Node>()
      .filter((event: any) => {
        // Only allow drag on left mouse button, not right-click
        return event.button === 0 || event.button === undefined;
      })
      .on('start', dragstarted)
      .on('drag', dragged)
      .on('end', dragended);
    
    node.call(drag as any);

    // Add circles
    node.append('circle')
      .attr('r', (d) => 
        typeof style.nodeSize === 'function' ? style.nodeSize(d) :
        d.size || style.nodeSize || 20
      )
      .attr('fill', (d) => 
        typeof style.nodeColor === 'function' ? style.nodeColor(d) :
        d.color || style.nodeColor || '#3b82f6'
      )
      .attr('stroke', (d) => d.id === highlightNode ? '#000' : '#fff')
      .attr('stroke-width', (d) => d.id === highlightNode ? 4 : 2)
      .attr('opacity', 0.8)
      .on('mouseenter', function (event, d) {
        d3.select(this).attr('opacity', 1);
        setHoveredNode(d.id);
        
        // Show tooltip
        showTooltip(event, renderNodeTooltip(d), d.label);
        
        if (onNodeHover) onNodeHover(d, event);
        
        // Highlight connected nodes/edges
        if (highlightConnected) {
          const connectedNodeIds = new Set<string>();
          links.forEach(l => {
            const source = typeof l.source === 'object' ? l.source.id : l.source;
            const target = typeof l.target === 'object' ? l.target.id : l.target;
            if (source === d.id) connectedNodeIds.add(target);
            if (target === d.id) connectedNodeIds.add(source);
          });

          node.selectAll('circle')
            .attr('opacity', (n: any) => 
              n.id === d.id || connectedNodeIds.has(n.id) ? 1 : 0.3
            );

          link.attr('opacity', (l: any) => {
            const source = typeof l.source === 'object' ? l.source.id : l.source;
            const target = typeof l.target === 'object' ? l.target.id : l.target;
            return source === d.id || target === d.id ? 1 : 0.2;
          });
        }
      })
      .on('mousemove', (event) => {
        updatePosition(event);
      })
      .on('mouseleave', function (event, d) {
        d3.select(this).attr('opacity', 0.8);
        setHoveredNode(null);
        
        // Hide tooltip
        hideTooltip();
        
        if (onNodeHover) onNodeHover(null, event);
        
        // Reset highlighting
        if (highlightConnected) {
          node.selectAll('circle').attr('opacity', 0.8);
          link.attr('opacity', 0.6);
        }
      })
      .on('click', (event, d) => {
        event.stopPropagation();
        if (onNodeClick) onNodeClick(d, event);
      })
      .on('contextmenu', (event, d) => {
        event.preventDefault();
        event.stopPropagation();
        openContextMenu(event, { type: 'node', node: d });
      });

    // Add labels
    if (showLabels) {
      node.append('text')
        .text((d) => d.label)
        .attr('text-anchor', 'middle')
        .attr('dy', (d) => {
          const radius = typeof style.nodeSize === 'function' ? style.nodeSize(d) :
            d.size || style.nodeSize || 20;
          return radius + 15;
        })
        .attr('font-size', style.fontSize || '12px')
        .attr('font-family', style.fontFamily || 'sans-serif')
        .attr('fill', '#333')
        .attr('pointer-events', 'none');
    }

    // Update positions on simulation tick
    simulation.on('tick', () => {
      link
        .attr('x1', (d: any) => d.source.x)
        .attr('y1', (d: any) => d.source.y)
        .attr('x2', (d: any) => d.target.x)
        .attr('y2', (d: any) => d.target.y);

      node.attr('transform', (d) => `translate(${d.x},${d.y})`);
    });

    // Drag functions
    function dragstarted(event: any, d: D3Node) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      d.fx = d.x;
      d.fy = d.y;
    }

    function dragged(event: any, d: D3Node) {
      d.fx = event.x;
      d.fy = event.y;
    }

    function dragended(event: any, d: D3Node) {
      if (!event.active) simulation.alphaTarget(0);
      d.fx = null;
      d.fy = null;
    }

    // Cleanup
    return () => {
      simulation.stop();
    };

  }, [data.nodes, data.edges, width, height]);
  // CRITICAL: Only restart simulation on data/size changes
  // nodeSpacing handled separately for smooth transitions

  // Handle nodeSpacing changes smoothly without full redraw
  useEffect(() => {
    const simulation = simulationRef.current;
    if (!simulation) return;

    // Update force strengths based on new nodeSpacing
    const linkForce = simulation.force('link') as d3.ForceLink<D3Node, D3Link>;
    const chargeForce = simulation.force('charge') as d3.ForceManyBody<D3Node>;
    
    if (linkForce) {
      linkForce.distance(forceOptions.linkDistance || nodeSpacing);
    }
    
    if (chargeForce) {
      chargeForce.strength(forceOptions.chargeStrength || -nodeSpacing);
    }

    // Gently restart simulation to apply new forces
    // Alpha 0.5 = medium energy, creates smooth animated transition
    // Nodes will gradually move to new positions over ~1-2 seconds
    simulation.alpha(0.5).restart();

  }, [nodeSpacing, forceOptions]);

  // Setup D3 zoom/pan behavior - SIMPLIFIED AND OPTIONAL
  useEffect(() => {
    if (!svgRef.current || !containerRef.current) return;
    if (!enableZoom) return;  // Skip zoom setup if disabled

    const svg = d3.select(svgRef.current);
    const container = d3.select(containerRef.current);

    // Simple zoom behavior - only on background, not on nodes
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.1, 4])
      .filter((event: any) => {
        // Only allow zoom on background clicks, not on nodes
        const target = event.target as Element;
        return target.tagName === 'svg';  // Only SVG background
      })
      .on('zoom', (event) => {
        container.attr('transform', event.transform);
      });

    svg.call(zoom);

    return () => {
      svg.on('.zoom', null);
    };
  }, [enableZoom]);
  
  // Separate effect for applying external zoom/pan changes
  useEffect(() => {
    if (!svgRef.current || !containerRef.current) return;
    
    const container = d3.select(containerRef.current);
    const transform = d3.zoomIdentity
      .translate(panOffset.x, panOffset.y)
      .scale(zoomLevel);
    
    container.attr('transform', transform.toString());
  }, [zoomLevel, panOffset]);

  // Handle spacing changes smoothly without restarting simulation
  useEffect(() => {
    const simulation = simulationRef.current;
    if (!simulation) return;
    
    // Update forces with new spacing values
    const linkForce = simulation.force('link') as d3.ForceLink<D3Node, D3Link>;
    const chargeForce = simulation.force('charge') as d3.ForceManyBody<D3Node>;
    
    if (linkForce) {
      linkForce.distance(nodeSpacing);
    }
    if (chargeForce) {
      // CORRECT LOGIC: Charge should scale WITH spacing, not inverse
      // Larger spacing = stronger repulsion (to maintain spread)
      // Smaller spacing = weaker repulsion (to allow compression)
      // At 50px: -50 (weak - allows tight packing)
      // At 150px: -150 (moderate)  
      // At 300px: -300 (strong - maintains spread)
      chargeForce.strength(-nodeSpacing);
    }
    
    // Gentle alpha bump to apply new forces without violent restart
    // This tells the simulation to re-evaluate positions with new forces
    if (simulation.alpha() < 0.1) {
      simulation.alpha(0.1);
    }
    
  }, [nodeSpacing]);

  // Handle label and highlight toggles without restarting simulation
  useEffect(() => {
    if (!svgRef.current || !containerRef.current) return;
    
    const container = d3.select(containerRef.current);
    
    // Toggle labels visibility
    container.selectAll('text')
      .style('display', showLabels ? 'block' : 'none');
    
    // Note: highlightConnected is handled in node hover events, not here
    // It affects styling during interactions, not static display
    
  }, [showLabels, highlightConnected]);

  return (
    <>
      <svg
        ref={svgRef}
        width={width}
        height={height}
        className={`force-directed-graph ${className}`}
        style={{
          border: '1px solid #ddd',
          borderRadius: '8px',
          background: style.backgroundColor || '#fafafa',
          touchAction: 'none',
        }}
      />
      
      {/* Tooltip */}
      <Tooltip
        isVisible={tooltip.isVisible}
        position={tooltip.position}
        content={tooltip.content}
        title={tooltip.title}
        onMouseEnter={cancelHide}  // Keep tooltip visible when hovering over it
        onMouseLeave={() => hideTooltip(true)}  // Hide immediately when leaving tooltip
      />
      
      {/* Context Menu */}
      {contextMenu.isOpen && contextMenu.data && (
        <ContextMenu
          isOpen={contextMenu.isOpen}
          position={contextMenu.position!}
          items={
            contextMenu.data.type === 'node'
              ? getNodeContextMenuItems(contextMenu.data.node)
              : getEdgeContextMenuItems(contextMenu.data.edge)
          }
          onClose={closeContextMenu}
        />
      )}
    </>
  );
};

export default ForceDirectedGraph;
